import { useCookieConsent } from '#app/context/CookieConsentContext.tsx';
import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { Link } from '@remix-run/react';

export default function CookieBanner() {
    const { consent, acceptCookies, rejectCookies } = useCookieConsent();
    const cookieConsentBox = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (consent === 'unknown' && cookieConsentBox.current) {
            // Ensure the banner starts off-screen to prevent flash
            gsap.set(cookieConsentBox.current, { y: '100%', opacity: 1 });

            const tl = gsap.timeline();

            // Animate the box from below the viewport to its final position
            tl.to(cookieConsentBox.current, {
                y: '0%',
                opacity: 1,
                duration: 1,
                ease: 'power2.out',
            });

            // Cleanup GSAP animation on component unmount
            return () => {
                tl.kill();
            };
        }
    }, [consent]);

    const close = () => {
        if (cookieConsentBox.current) {
            gsap.to(cookieConsentBox.current, {
                y: '100%',
                opacity: 0,
                duration: 0.5,
                onComplete: () => {
                    if (cookieConsentBox.current) {
                        cookieConsentBox.current.style.display = 'none';
                    }
                },
            });
        }
    };

    // Prevent rendering the banner unless consent is 'unknown'
    if (consent !== 'unknown') return null;

    return (
        <div
            ref={cookieConsentBox}
            className="fixed bottom-0 left-0 right-0 z-50 mx-auto w-full border-t-2 border-gray-300 bg-white px-6 py-5 text-gray-800"
            style={{ display: consent === 'unknown' ? 'block' : 'none' }} // Ensure initial hidden state
        >
            <div className="flex flex-col justify-center sm:flex-row sm:gap-10">
                <div className="text-xs">
                    <p className="font-bold">Help us improve our website with cookies</p>
                    <p>
                        We use cookies and process data from your device to analyze website performance, personalize ad content, and
                        improve your experience.
                    </p>
                    <p>
                        <Link to="/cookie-settings" className="underline">
                            View Cookie Settings
                        </Link>{' '}
                        for more information.
                    </p>
                </div>
                <div className="mt-4 flex flex-col sm:flex-row flex-wrap justify-end gap-2 sm:gap-4">
                    <div>
                        <button
                            onClick={() => {
                                acceptCookies();
                                close();
                            }}
                            className="h-8 rounded bg-blue-600 px-6 py-1.5 text-sm font-medium leading-4 text-white hover:bg-blue-700 w-full"
                        >
                            Accept
                        </button>
                    </div>
                    <div>
                        <button
                            onClick={() => {
                                rejectCookies();
                                close();
                            }}
                            className="h-8 rounded bg-gray-200 px-6 py-1.5 text-sm font-medium leading-4 text-gray-800 hover:bg-gray-300 w-full"
                        >
                            Reject
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
